/**
 * StatResolver — three-tier modifier resolution pipeline.
 *
 * Game-agnostic. Resolves a base value through three layers:
 *   1. Flat    — summed and added to base
 *   2. Percent — summed into one additive %, applied to (base + flat)
 *   3. Mult    — each multiplied independently onto the result
 *
 * Resolution:
 *   result = (base + sumFlat) * (1 + sumPercent) * mult1 * mult2 * ...
 *
 * This matches the industry-standard pattern used by Path of Exile,
 * Slay the Spire, and most stat-heavy RPGs.
 */

/** The three modifier operations, applied in this order. */
export type ModOp = "flat" | "percent" | "mult";

/** A single modifier entry. */
export interface Modifier {
  /** Which operation tier this modifier belongs to. */
  op: ModOp;
  /** The modifier value (e.g. 5 for +5 flat, 0.2 for +20%, 1.5 for x1.5). */
  value: number;
  /** Human-readable source for damage breakdowns / tooltips. */
  source: string;
}

/**
 * Resolve a base value through a list of modifiers.
 *
 * @param base      - The unmodified base value
 * @param modifiers - All modifiers to apply
 * @param floor     - If true (default), result is floored to integer
 */
export function resolve(base: number, modifiers: Modifier[], floor = true): number {
  let sumFlat = 0;
  let sumPercent = 0;
  let product = 1;

  for (const mod of modifiers) {
    switch (mod.op) {
      case "flat":    sumFlat += mod.value; break;
      case "percent": sumPercent += mod.value; break;
      case "mult":    product *= mod.value; break;
    }
  }

  const result = (base + sumFlat) * (1 + sumPercent) * product;
  return floor ? Math.floor(result) : result;
}

/**
 * Return a breakdown of how the value was computed.
 * Useful for tooltips and damage breakdowns.
 */
export function breakdown(base: number, modifiers: Modifier[]): ResolveBreakdown {
  const flats: Modifier[] = [];
  const percents: Modifier[] = [];
  const mults: Modifier[] = [];

  for (const mod of modifiers) {
    switch (mod.op) {
      case "flat":    flats.push(mod); break;
      case "percent": percents.push(mod); break;
      case "mult":    mults.push(mod); break;
    }
  }

  const sumFlat = flats.reduce((s, m) => s + m.value, 0);
  const sumPercent = percents.reduce((s, m) => s + m.value, 0);
  const product = mults.reduce((p, m) => p * m.value, 1);
  const final = Math.floor((base + sumFlat) * (1 + sumPercent) * product);

  return { base, flats, percents, mults, sumFlat, sumPercent, product, final };
}

export interface ResolveBreakdown {
  base: number;
  flats: Modifier[];
  percents: Modifier[];
  mults: Modifier[];
  sumFlat: number;
  sumPercent: number;
  product: number;
  final: number;
}
